/**
 * Custom hook for managing chat messaging functionality
 * Follows ADR-004: Separation of Concerns - Business logic layer
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { logError } from '../utils/logger';
import { isBotMacroCommand, executeCommand } from '../services/BotMacros';
import type { EventCreationParams } from 'parlant-client/src/api';
import type { MessageInterface } from './useMessageProcessing';

/**
 * Options for useChatMessaging hook
 */
interface UseChatMessagingOptions {
  /** Server URL */
  serverUrl: string;
  /** Session ID */
  sessionId: string | null;
  /** Authentication token */
  authToken?: string;
  /** Callback to create session */
  createSession: (message?: EventCreationParams) => Promise<void>;
  /** Callback to inject messages directly (for bot macros) */
  onInjectMessages?: (messages: MessageInterface[]) => void;
  /** External input message value (for controlled state) */
  externalInputMessage?: string;
  /** Callback for input message changes (for controlled state) */
  onInputMessageChange?: (message: string) => void;
}

/**
 * Return value for useChatMessaging hook
 */
interface UseChatMessagingReturn {
  /** Current input message */
  inputMessage: string;
  /** Set input message */
  setInputMessage: (message: string) => void;
  /** Send a message from the input field */
  sendMessage: () => Promise<void>;
  /** Send a message directly without using input state (for programmatic messages like button clicks) */
  sendDirectMessage: (content: string) => Promise<void>;
  /** Handle form submission (prevents default and calls sendMessage) */
  handleFormSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  /** 
   * Handle key press (deprecated - use handleFormSubmit with form element instead)
   * @deprecated Prefer using handleFormSubmit with a form element for better accessibility
   */
  handleKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * Custom hook for managing chat messaging functionality
 * 
 * @param options - Messaging configuration options
 * @returns Messaging functions and state
 * 
 * @example
 * ```tsx
 * const { inputMessage, setInputMessage, sendMessage } = useChatMessaging({
 *   serverUrl: 'https://api.example.com',
 *   sessionId: 'session-123',
 *   authToken: 'token...',
 *   createSession: createSessionFn
 * });
 * ```
 */
export const useChatMessaging = ({
  serverUrl,
  sessionId,
  authToken,
  createSession,
  onInjectMessages,
  externalInputMessage,
  onInputMessageChange
}: UseChatMessagingOptions): UseChatMessagingReturn => {
  // Internal state for uncontrolled mode
  const [internalInputMessage, setInternalInputMessage] = useState<string>(
    // Initialize with external value if provided (preserves value on mode switch)
    externalInputMessage ?? ''
  );
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Use external state if provided (controlled mode), otherwise use internal state
  const isControlled = externalInputMessage !== undefined && onInputMessageChange !== undefined;
  const inputMessage = isControlled ? externalInputMessage : internalInputMessage;
  const setInputMessage = isControlled ? onInputMessageChange : setInternalInputMessage;

  // Sync internal state with external value when it changes
  // This ensures the value persists when switching between popup and fullscreen modes
  useEffect(() => {
    if (externalInputMessage !== undefined && externalInputMessage !== internalInputMessage) {
      setInternalInputMessage(externalInputMessage);
    }
  }, [externalInputMessage, internalInputMessage]);

  /**
   * Sends a message
   */
  const postMessage = useCallback(
    async (content: string): Promise<void> => {
      if (!content.trim()) return;

      const message: EventCreationParams = {
        kind: 'message',
        message: content,
        source: 'customer'
      };

      if (sessionId) {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        };

        if (authToken) {
          headers['Authorization'] = `Bearer ${authToken}`;
        }

        const response = await fetch(`${serverUrl}/sessions/${sessionId}/events`, {
          method: 'POST',
          headers,
          body: JSON.stringify(message)
        });

        if (!response.ok) {
          const errorData = await response.json();

          // Provide specific error messages for auth failures
          if (response.status === 401) {
            throw new Error('Authentication failed: Invalid or expired token. Please log in again.');
          }
          if (response.status === 403) {
            throw new Error('Access denied: You do not have permission to send messages.');
          }

          throw new Error(`Failed to send message: HTTP ${response.status}: ${JSON.stringify(errorData)}`);
        }
      } else {
        await createSession(message);
      }
    },
    [sessionId, serverUrl, createSession, authToken]
  );

  /**
   * Sends a message from the input field
   */
  const sendMessage = useCallback(async (): Promise<void> => {
    if (!inputMessage.trim()) return;

    const messageText = inputMessage.trim();
    setInputMessage('');

    setTimeout(() => {
      inputRef.current?.focus();
    }, 10);

    // Check if this is a bot macro command
    if (isBotMacroCommand(messageText)) {
      const result = executeCommand(messageText);
      
      if (result.handled) {
        if (result.error) {
          // Show error as a message from the agent
          if (onInjectMessages) {
            onInjectMessages([{
              id: `error-${Date.now()}`,
              kind: 'message',
              source: 'ai_agent',
              creationUtc: new Date(),
              offset: Date.now(),
              correlationId: `error-${Date.now()}`,
              data: { message: `❌ **Error:** ${result.error}` },
              status: 'ready',
            }]);
          }
        } else if (result.messages && result.messages.length > 0) {
          // Inject the generated messages
          if (onInjectMessages) {
            onInjectMessages(result.messages);
          }
        }
        
        // Only send to backend if the command explicitly requests it
        if (!result.sendToBackend) {
          return; // Don't send to server
        }
        // If sendToBackend is true, continue to send the message below
      }
    }

    try {
      await postMessage(messageText);
    } catch (error) {
      logError('Failed to send message:', error);
      throw error;
    }
  }, [inputMessage, setInputMessage, postMessage, onInjectMessages]);

  /**
   * Sends a message directly without using input state
   * Use this for programmatic messages like button click replies
   * 
   * @param content - The message content to send
   */
  const sendDirectMessage = useCallback(async (content: string): Promise<void> => {
    if (!content.trim()) return;

    // Check if this is a bot macro command
    if (isBotMacroCommand(content)) {
      const result = executeCommand(content);
      
      if (result.handled) {
        if (result.error) {
          // Show error as a message from the agent
          if (onInjectMessages) {
            onInjectMessages([{
              id: `error-${Date.now()}`,
              kind: 'message',
              source: 'ai_agent',
              creationUtc: new Date(),
              offset: Date.now(),
              correlationId: `error-${Date.now()}`,
              data: { message: `❌ **Error:** ${result.error}` },
              status: 'ready',
            }]);
          }
        } else if (result.messages && result.messages.length > 0) {
          // Inject the generated messages
          if (onInjectMessages) {
            onInjectMessages(result.messages);
          }
        }
        
        // Only send to backend if the command explicitly requests it
        if (!result.sendToBackend) {
          return; // Don't send to server
        }
        // If sendToBackend is true, continue to send the message below
      }
    }

    try {
      await postMessage(content.trim());
    } catch (error) {
      logError('Failed to send direct message:', error);
      throw error;
    }
  }, [postMessage, onInjectMessages]);

  /**
   * Handles form submission (Enter key or button click)
   * Use this with a form element for proper accessibility and automation support.
   * 
   * Reads the message from FormData as a fallback for browser automation tools
   * that may not trigger React's onChange events properly.
   * 
   * @param event - The form submission event
   */
  const handleFormSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      
      // Try to get value from FormData as fallback for automation tools
      // that type directly into DOM without triggering React events
      const formData = new FormData(event.currentTarget);
      const formMessage = formData.get('message') as string | null;
      
      // Use form value if React state is empty but form has content
      if (!inputMessage.trim() && formMessage?.trim()) {
        // Sync the form value to React state and send
        setInputMessage(formMessage);
        // Send the message directly since state update is async
        postMessage(formMessage.trim()).then(() => {
          setInputMessage('');
        }).catch((error) => {
          logError('Failed to send message:', error);
        });
        return;
      }
      
      sendMessage();
    },
    [sendMessage, inputMessage, setInputMessage, postMessage]
  );

  /**
   * Handles key press events in the input field
   * @deprecated Prefer using handleFormSubmit with a form element for better accessibility
   */
  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  return {
    inputMessage,
    setInputMessage,
    sendMessage,
    sendDirectMessage,
    handleFormSubmit,
    handleKeyPress
  };
};

